import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { DefaultTheme, useTheme } from "styled-components/native";
import { Animated, Platform, ScrollView } from "react-native";
import { Container, Spacer, VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useTypographyStore } from "@redux/slices/typographySlice";
import ButtonsContainer from "./utils/ButtonsContainer";
import { useTranslation } from "react-i18next";
import {
  setProductTypeFromRequest,
  setProductTypeFromStockType,
} from "./utils/RequestsScreen";
import {
  useRequestChildrenAPI,
  useRequestHistoryDetail,
  useUpdateReadRequestStatus,
} from "@apis/requestHistory";
import { MemoStatus, StockItemRequestClass } from "@src/types/HistoryRequest";
import Loader from "@components/atoms/Loader";
import RequestViewParentCard from "@components/organisms/RequestViewParentCard";
import { navigate, reset } from "@navigations/utils/RootNavigation";
import {
  enrichStockItemRequest,
  getFirstNonNullNote,
  getOrderStockType,
  getRequestType,
  isAnyChildWithdrawn,
  isValidValue,
  normalizeOrderResponse,
  requestTypeMap,
  RequestTypes,
  showRequestItemFromSupplier,
} from "./utils/utils";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import CompanyDetailsCard from "./utils/CompanyDetailsCard";
import VDBIcon, { IconsType, IconsVariant } from "@components/icons/VDBIcon";
import { messageType } from "@components/molecules/PopUp/types";
import ReasonContainer from "./utils/ReasonContainer";
import { getRequestIconMap } from "@components/molecules/RequestHistory/utils/utils";
import { useFocusEffect } from "@react-navigation/native";
import LayoutContainer from "@components/layout/LayoutContainer";
import { useScrollContext } from "@navigations/utils/ScrollContext";
import Routes from "@navigations/Routes";
import { useQueryClient } from "@tanstack/react-query";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import {
  getHeaderStyleBasedOnProduct,
  getPriceModeColorCode,
} from "@components/common/Header/utils";
import WebPageHeader from "@components/molecules/WebPageHeader/WebPageHeader";
import {
  SelectedItemsProvider,
  useSelectedItems,
} from "../../contexts/SelectedItemsContext";
import {
  getBoxShadow,
  handleSupplierForPaUser,
  isPair,
  isParcel,
  isMeleeItem,
} from "@utils/product/productValidators";
import RequestStatusCard from "@scenes/requests/RequestStatusCard";
import CommentSection from "@scenes/requests/CommentSection";
import StoreCustomerAttributes from "@scenes/requests/StoreAttributesCard";
import LineViewContainer from "./utils/LineView";
import { ProductCategory, ProductTypeCommon, ViewType } from "@utils/constants";
import {
  useConsolidatedStoneTypes,
  useProducts,
} from "@redux/slices/globalSlice";
import { DynamicFormProvider } from "@scenes/bulk-request/context/DynamicFormContext";
import { useProductPrefix } from "../../hooks/useProductPrefix";
import TotalPriceCard from "./TotalPriceCard";
import OrderRequestScreen from "./OrderRequestScreen";
import HorizontalScrollWrapper from "@scenes/searchResults/components/HorizontalScrollWrapper";
import { useSharedValue } from "react-native-reanimated";
import TabletListHeader from "@components/molecules/SearchResultListItem/tabletListHeader";
import { SearchResultProvider } from "@components/molecules/SearchResultListItem/SearchResultContext";
import { useUserCurrency } from "../../hooks/getCurrency";
import RequestDetailTabletListItem from "./RequestDetailTabletListItem";
import ItemActionCard from "./ItemActionCard";
import ActionCard from "./ActionCard";
import currency from "currency.js";
import { GetHeaderLeft } from "@navigations/utils";
import getPriceMode from "@navigations/utils/getPriceMode";
import { useSelector } from "react-redux";
import { PdpSections } from "@utils/customization";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import {
  SearchFilterTypeEnum,
  SearchFilterTypeEnumLowercase,
} from "@components/atoms/Filters/utils";
import { StoneType } from "@scenes/bulk-request/Utils";
import {
  getProductName,
  getRequestProductName,
} from "@utils/product/productNames";
import { VDBIcons } from "@utils/VDBIcons";

const RequestDetailScreen = ({ route, navigation }: any) => {
  const xOffset = useSharedValue(0);
  const typography = useTypographyStore();
  const theme = useTheme();
  const { t } = useTranslation();
  const userInfo = useUserInfo();
  const stockItemRequestId = route.params?.id ?? route.params?.order_id;
  const requestName = route.params?.requestName;
  const mExternalPriceMode = route.params?.externalPriceMode;
  const stockItemRequestCall = useRequestHistoryDetail(
    stockItemRequestId,
    requestName,
    userInfo.user.currency,
    userInfo?.getPriceMode()
  );
  const orderPayload = stockItemRequestCall.data?.data;
  const orderedItemsReady = orderPayload?.response?.order?.ordered_items_ready;
  const shouldWaitForOrderedItems =
    requestName === RequestTypes.Order && orderedItemsReady === false;
  const orderIdFromParamsRef = useRef(stockItemRequestId);
  orderIdFromParamsRef.current = stockItemRequestId;
  const wasWaitingForOrderedItemsRef = useRef(false);
  const normalizedData = normalizeOrderResponse(
    stockItemRequestCall?.data?.data?.response,
    requestTypeMap(t)[
      getOrderStockType(stockItemRequestCall.data?.data.response?.[requestName])
    ]?.name || "Multiple Items"
  );
  const updateRead = useUpdateReadRequestStatus(stockItemRequestId);
  const getChildrenAPI = useRequestChildrenAPI(stockItemRequestId);

  const queryClient = useQueryClient();
  const [stockItemRequest, setStockItemRequest] =
    useState<StockItemRequestClass | null>();
  const [childValues, setChildValues] = useState<[]>();
  const [hasAnyOfferDetails, setHasAnyOfferDetails] = useState(false);
  const { enableJewelryBuilder } = useOrgSettingsSlice();
  const isBuilderItem =
    enableJewelryBuilder &&
    stockItemRequest?.secondary_request_type === PdpSections.JEWELRY_BUILDER;
  const [screenTitle, setScreenTitle] = useState("");
  const [validValue, setValidValue] = useState(false);
  const hasChildren =
    requestName === RequestTypes.Order
      ? true
      : isBuilderItem
      ? false
      : !!stockItemRequest?.children?.length;
  const [disabledButton, setDisabledButton] = useState(false);
  const isCustomApp = useSelector((state: any) => state.appState?.isCustomApp);
  const productPrefix = useProductPrefix();
  const userCurrency = useUserCurrency();
  const { getUserPriceMode } = useUserInfo();
  const consolidatedStoneType = useConsolidatedStoneTypes();
  const showCompanyDetails = useMemo(() => {
    return (
      userInfo.user.price_mode === PriceModes.WHITE ||
      isCustomApp ||
      !(userInfo.user.price_mode === PriceModes.ORANGE) ||
      (mExternalPriceMode !== undefined &&
        (mExternalPriceMode === PriceModes.WHITE ||
          !(mExternalPriceMode === PriceModes.ORANGE)))
    );
  }, [userInfo.user.price_mode, isCustomApp, mExternalPriceMode]);

  const [reasonContainer, setReasonContainer] = useState({
    type: messageType.Error,
    title: "",
    comments: "-",
  });
  const [productType, setProductType] = useState({
    productType: ProductCategory.DEFAULT,
    bulkProductName: "",
    productIcon: "",
    theme: "",
  });

  const isLooseParcel = stockItemRequest?.[productType?.productType]
    ?.diamond_category
    ? stockItemRequest?.[productType?.productType]?.diamond_category ===
      SearchFilterTypeEnumLowercase.LOOSE_PARCEL
    : false;

  const allProducts = useProducts();
  const getProducts = useMemo(() => allProducts, [allProducts]);
  const matchedProduct = getProducts?.find(
    (item) => item?.key === productType.productType
  );
  const icon = matchedProduct?.sub_type?.find(
    (subType: any) =>
      subType.name === stockItemRequest?.product_sub_type?.toLowerCase()
  )?.icon;

  const iconData = matchedProduct
    ? icon?.data || matchedProduct?.icon?.data
    : null;

  const hideListPrice = () => {
    const updatedPrice = stockItemRequest?.offer_total_sales_price;
    if (!updatedPrice) return true;

    const precision =
      userInfo?.roundUpPrices() &&
      !isMeleeItem(stockItemRequest?.[productType.productType])
        ? 0
        : 2;

    if (route.params?.skip_unread === undefined || !route.params?.skip_unread) {
      const children = stockItemRequest?.children || [];
      const hasOfferInChildren = children.some(
        (child) => child?.offer_discount_percent !== null
      );

      if (hasOfferInChildren) return true;
    } else if (route.params?.skip_unread) {
      const isTotalOfferPriceNotApplied =
        !stockItemRequest?.quote_details?.total_offer_price_applied ?? false;
      const isOfferDiscountPercentAvailable =
        stockItemRequest?.offer_discount_percent !== null;

      return isTotalOfferPriceNotApplied || isOfferDiscountPercentAvailable;
    }

    return (
      currency(updatedPrice, {
        symbol: userCurrency,
        precision,
      }).format() ===
      currency(stockItemRequest?.total_listed_price, {
        symbol: userCurrency,
        precision,
      }).format()
    );
  };

  const isTotalOfferDiscountApplied = () => {
    const children = stockItemRequest?.children;

    if (!Array.isArray(children) || children.length === 0) {
      return false;
    }

    const firstDiscount = children[0]?.offer_discount_percent;
    if (firstDiscount == null) {
      return false;
    }
    return children?.every(
      (item) => item?.offer_discount_percent === firstDiscount
    );
  };

  useEffect(() => {
    if (userInfo?.getPriceMode() === PriceModes.GREEN) {
      handleNavigation();
    }
  }, [userInfo?.getPriceMode()]);

  useFocusEffect(
    useCallback(() => {
      if (!shouldWaitForOrderedItems || !stockItemRequestId) {
        return;
      }

      const scheduledOrderId = stockItemRequestId;
      const timeoutId = setTimeout(() => {
        if (orderIdFromParamsRef.current === scheduledOrderId) {
          stockItemRequestCall.refetch();
        }
      }, 10000);

      return () => clearTimeout(timeoutId);
    }, [
      shouldWaitForOrderedItems,
      stockItemRequestId,
      stockItemRequestCall.refetch,
      stockItemRequestCall.dataUpdatedAt,
    ])
  );

  useEffect(() => {
    if (shouldWaitForOrderedItems) {
      wasWaitingForOrderedItemsRef.current = true;
      return;
    }
    if (
      wasWaitingForOrderedItemsRef.current &&
      requestName === RequestTypes.Order &&
      orderedItemsReady === true
    ) {
      wasWaitingForOrderedItemsRef.current = false;
      queryClient.invalidateQueries({ queryKey: ["stock_item_request"] });
    }
  }, [shouldWaitForOrderedItems, orderedItemsReady, requestName, queryClient]);

  const handleNavigation = () => {
    reset(Routes.RequestHistory);
  };

  const isHideOtherButtonVisible = () =>
    getRequestType(stockItemRequest?.request_type) === RequestTypes.Quote &&
    route.params?.skip_unread === true &&
    isTotalOfferDiscountApplied() === false &&
    (hideListPrice() === false ||
      stockItemRequest?.quote_details?.total_offer_price_applied);

  const [_, handleReset, ___, ____, _____, ______] = useScrollContext();
  const { selectedItems, setSelectedItems } = useSelectedItems();
  const routeProductType = route?.params?.productType;
  const headerProductType =
    routeProductType && routeProductType !== ProductCategory.DEFAULT
      ? routeProductType
      : productType?.productType || ProductCategory.DEFAULT;
  const { background, textColor } = getHeaderStyleBasedOnProduct(
    headerProductType,
    theme
  );

  const computeIndividualItemUpdates = (children: any[] = []) => {
    return children.map((child) => {
      const offerPPC = parseFloat(child.offer_price_per_carat ?? 0);
      const itemPPC = parseFloat(child?.item?.price_per_carat ?? 0);
      const offerTSP = parseFloat(child.offer_total_sales_price ?? 0);
      const itemTSP = parseFloat(child?.item?.total_sales_price ?? 0);

      const individualItemEdit = offerPPC !== itemPPC || offerTSP !== itemTSP;
      return {
        id: child.id,
        parent_id: child?.item?.id,
        IndividualItemEdit: individualItemEdit,
      };
    });
  };

  useEffect(() => {
    if (mExternalPriceMode || background) {
      navigation.setOptions({
        headerStyle: {
          borderBottomColor: !userInfo.userCanSetMarkupInOrangeMode()
            ? theme[
                getPriceModeColorCode(
                  mExternalPriceMode
                    ? mExternalPriceMode
                    : userInfo.user.price_mode
                )
              ]
            : theme["core-04"],
          borderBottomWidth:
            userInfo.userCanSetMarkupInOrangeMode() ||
            (mExternalPriceMode
              ? mExternalPriceMode === PriceModes.WHITE
              : userInfo.user.price_mode === PriceModes.WHITE)
              ? 1
              : 4,
          backgroundColor: background
            ? theme[background as keyof DefaultTheme]
            : theme["core-03"],
        },
      });
    }
  }, [
    mExternalPriceMode,
    background,
    userInfo.user.price_mode,
    productType.theme,
  ]);

  useEffect(() => {
    handleReset();
  }, [productType?.productType]);

  useEffect(() => {
    if (stockItemRequestCall.isFetching) setStockItemRequest(null);
    if (
      stockItemRequestCall.isFetched &&
      stockItemRequestCall.data?.data?.data?.stock_item_request?.id &&
      requestName !== RequestTypes.Order
    ) {
      const request = stockItemRequestCall.data?.data?.data?.stock_item_request;
      const { productName } = getRequestProductName(
        request?.stock_item_type,
        request?.[request?.stock_item_type],
        t,
        request?.children?.length > 0,
        isCustomApp
      );
      let data = {};
      const enrichedRequest = enrichStockItemRequest(
        request,
        request?.stock_item_type,
        t,
        isCustomApp
      );
      setStockItemRequest(
        enrichedRequest || {
          ...request,
          type_text: productName || "",
        }
      );
      if (request.view_type === "buyer_view") {
        data =
          requestName === RequestTypes.Quote
            ? { read: true }
            : { read_by_buyer: true };
      } else {
        data = { read: true };
      }

      if (!route.params?.skip_unread)
        updateRead.mutate(data, {
          onSuccess() {
            queryClient
              .invalidateQueries({ queryKey: ["GET_UNREAD_REQUEST"] })
              .then();
          },
        });
      data = {
        ...data,
        price_mode: userInfo?.getPriceMode(),
      };
      getChildrenAPI.mutate(data, {
        onSuccess(childData: any) {
          const individualItemValid = computeIndividualItemUpdates(
            childData?.data?.data?.children
          );
          setChildValues(individualItemValid);
          const hasAnyEdit = individualItemValid.some(
            (item) => item.IndividualItemEdit === true
          );
          setHasAnyOfferDetails(hasAnyEdit);
        },
      });
      renderStockDetail(request);
    } else if (requestName === RequestTypes.Order) {
      renderQuoteDetail();
      setStockItemRequest({
        ...normalizedData,
        type_text:
          requestTypeMap(t)[
            getOrderStockType(
              stockItemRequestCall.data?.data.response?.[requestName]
            )
          ]?.name || "",
      });
    }
  }, [
    stockItemRequestCall.data?.data?.data?.stock_item_request?.id,
    stockItemRequestCall.dataUpdatedAt,
    t,
    userInfo.getPriceMode(),
  ]);

  useEffect(() => {
    if (stockItemRequest?.id && requestName !== RequestTypes.Order)
      renderStockDetail(stockItemRequest);
  }, [stockItemRequest?.id]);

  useEffect(() => {
    if (theme.isTablet || theme.isLargeScreen) {
      navigation.setOptions({
        headerLeft: () => {},
      });
    }
  }, []);

  useEffect(() => {
    if (
      !route.params?.skip_unread &&
      (getRequestType(stockItemRequest?.request_type ?? "") ===
        RequestTypes.Quote ||
        (getRequestType(stockItemRequest?.request_type ?? "") ===
          RequestTypes.Buy &&
          hasChildren))
    ) {
      if (selectedItems?.length === 0) {
        setDisabledButton(true);
      } else {
        setDisabledButton(false);
      }
    }
  }, [selectedItems]);

  const renderStockDetail = (request: any) => {
    const requestIconMap = getRequestIconMap(t);
    setProductType(
      setProductTypeFromRequest(
        request?.request_type,
        stockItemRequest?.[productType?.productType]?.sub_type
      )
    );
    const requestType =
      getRequestIconMap(t)[request.request_type]?.request_name;
    const isPurchasingAgent =
      stockItemRequest?.bmh_request_type === "purchasing_agent";

    let titlePrefix = "";

    if (isPurchasingAgent) {
      if (requestName === RequestTypes.diamondMemo) {
        titlePrefix = `${t("requestHistoryDetail.title.text")}`;
      } else if (
        requestName === RequestTypes.Memo ||
        requestName === RequestTypes.Internal
      ) {
        titlePrefix = t("requestHistoryDetail.internalRequest.title.text");
      } else {
        titlePrefix = `${requestType || ""} ${t(
          "requestHistoryDetail.title.text"
        )}`;
      }
    } else {
      if (
        getRequestType(stockItemRequest?.request_type ?? "") ===
          RequestTypes.Memo &&
        stockItemRequest?.memo_status
      ) {
        if (stockItemRequest?.memo_status === MemoStatus.Sold) {
          titlePrefix = t("requestHistoryDetail.sold.title.text");
        } else if (stockItemRequest?.memo_status === MemoStatus.Return) {
          titlePrefix = t("requestHistoryDetail.return.title.text");
        } else {
          titlePrefix = `${requestType || ""} ${t(
            "requestHistoryDetail.title.text"
          )}`;
        }
      } else {
        titlePrefix = `${requestType || ""} ${t(
          "requestHistoryDetail.title.text"
        )}`;
      }
    }

    const title = titlePrefix;

    setScreenTitle(title);
    const focusedRouteName = route.name;
    const priceModeConfig = getPriceMode({
      focusedRouteName,
      background,
      textColor,
      user: userInfo.user,
      theme,
    });
    setTimeout(() => {
      setTimeout(() => {
        navigation.setOptions({
          headerTitle: title,
          headerTintColor: theme[`${productType.theme}-05`],
        });
      });
      navigation.setOptions({
        headerRight: () => (
          <VDBIcon
            variant={IconsVariant.VDBCustom}
            name={
              userInfo.isPAUserOrPASalesPerson() &&
              userInfo.isOrangePriceMode() &&
              (requestIconMap[request.request_type]?.request_icon ===
                RequestTypes.Memo.toLowerCase() ||
                requestIconMap[request.request_type]?.request_icon ===
                  RequestTypes.Internal.toLowerCase())
                ? RequestTypes.Memo.toLowerCase()
                : stockItemRequest?.memo_status === MemoStatus.Sold
                ? VDBIcons.BUY
                : stockItemRequest?.memo_status === MemoStatus.Return
                ? VDBIcons.REFRESH_CIRCLE
                : requestIconMap[request.request_type]?.request_icon
            }
            color={theme[`${productType.theme}-05`]}
            size={24}
          />
        ),
        ...(route.params?.skip_unread === true &&
          !theme.isMobile && {
            headerLeft: () => (
              <GetHeaderLeft canGoBack={true} textColor={textColor} />
            ),
          }),
        ...priceModeConfig,
      });
    });

    if (request?.status === "decline") {
      setReasonContainer({
        ...reasonContainer,
        title: t("requestHistoryDetail.declineReason.title.text"),
        comments: request?.notes || "-",
      });
    } else if (isAnyChildWithdrawn(request) || request?.status === "withdraw") {
      setReasonContainer({
        ...reasonContainer,
        title: t("requestHistoryDetail.withdrawReason.title.text"),
        comments: getFirstNonNullNote(request) || request?.notes || "-",
      });
    } else {
      setReasonContainer({
        ...reasonContainer,
        title: "",
      });
    }
  };

  const renderQuoteDetail = () => {
    const order = stockItemRequestCall.data?.data?.response?.order;
    const isQuoteOrder =
      String(order?.order_type ?? "").toLowerCase() === "quote";
    const isOrderCartQuoteDetail =
      isQuoteOrder &&
      (order?.view_type === ViewType.SELLER ||
        (order?.view_type === ViewType.BUYER &&
          userInfo.enableOrderCartQuote() === true));
    const title = isOrderCartQuoteDetail
      ? t("requestHistoryDetail.quoteDetails.title.text", "Quote Details")
      : t("requestHistoryDetail.orders.title.text");
    const resolvedProduct = setProductTypeFromStockType(
      getOrderStockType(stockItemRequestCall.data?.data.response?.[requestName])
    );
    setProductType(resolvedProduct);
    setScreenTitle(title);
    const headerCategory =
      route?.params?.productType &&
      route?.params?.productType !== ProductCategory.DEFAULT
        ? route.params.productType
        : resolvedProduct.productType;
    const { background: headerBackground, textColor: headerTextColor } =
      getHeaderStyleBasedOnProduct(headerCategory, theme);
    const headerTint = theme[headerTextColor as keyof DefaultTheme] as string;
    const priceModeConfig = getPriceMode({
      focusedRouteName: route.name,
      background: headerBackground,
      textColor: headerTextColor,
      user: userInfo.user,
      theme,
    });
    setTimeout(() => {
      setTimeout(() => {
        navigation.setOptions({
          headerTitle: title,
          ...priceModeConfig,
          headerTintColor: headerTint,
          ...(isOrderCartQuoteDetail && {
            headerRight: () => (
              <VDBIcon
                variant={IconsVariant.VDBCustom}
                name={VDBIcons.QUOTE}
                color={headerTint}
                size={24}
              />
            ),
          }),
        });
      });
    });
  };

  useEffect(() => {
    if (stockItemRequest) {
      const isValid = isValidValue(stockItemRequest.offer_total_sales_price);
      setValidValue(isValid);
    }
  }, [stockItemRequest]);

  const modifyRequest = () => {
    const individualItemUpdated = computeIndividualItemUpdates(
      stockItemRequest?.children
    );
    const updatedStockItemRequest = {
      ...stockItemRequest,
      children: stockItemRequest?.children?.map((item) => {
        const match = individualItemUpdated.find(
          (update) => update.id === item.id
        );
        return {
          ...item,
          IndividualItemEdit: match ? match.IndividualItemEdit : false,
        };
      }),
    };
    navigate(Routes.RequestModifyScreen, {
      stockItemRequest: updatedStockItemRequest,
      productType,
      externalPriceMode: mExternalPriceMode,
      checkboxBlock: hideListPrice(),
      isTotalOfferDiscountApplied: isTotalOfferDiscountApplied(),
      childValues: childValues,
    });
  };

  useFocusEffect(
    useCallback(() => {
      stockItemRequestCall.refetch();
      return () => {};
    }, [])
  );

  const titleIcon = useMemo(() => {
    const order = stockItemRequestCall.data?.data?.response?.order;
    const isQuoteOrder =
      String(order?.order_type ?? "").toLowerCase() === "quote";
    const isOrderCartQuoteDetail =
      requestName === RequestTypes.Order &&
      isQuoteOrder &&
      (order?.view_type === ViewType.SELLER ||
        (order?.view_type === ViewType.BUYER &&
          userInfo.enableOrderCartQuote() === true));
    if (requestName === RequestTypes.Order && !isOrderCartQuoteDetail) {
      return null;
    } else if (isOrderCartQuoteDetail) {
      return (
        <VDBIcon
          name={VDBIcons.QUOTE}
          size={24}
          color={theme["main-01"]}
          variant={IconsVariant.VDBCustom}
        />
      );
    } else {
      let requestType =
        stockItemRequestCall.data?.data?.data?.stock_item_request?.request_type?.toLowerCase();
      let firstPart = requestType?.toLowerCase()?.split("_")?.[0];
      let lastPart = requestType?.toLowerCase()?.split("_")?.at(-1);

      const normalizedFirst = firstPart?.toLowerCase();
      const normalizedLast = lastPart?.toLowerCase();
      const normalizedType = requestType?.toLowerCase();

      const typeMap: Record<string, string> = {
        buy: RequestTypes.Buy.toLowerCase(),
        memo: RequestTypes.Memo.toLowerCase(),
        hold: RequestTypes.Hold.toLowerCase(),
        ask: "ask-request",
        quote: RequestTypes.Quote.toLowerCase(),
        sold: RequestTypes.Buy.toLowerCase(),
        return: VDBIcons.REFRESH_CIRCLE,
      };

      let iconName: string | null = null;

      if (normalizedType?.includes("bulk_buy")) {
        iconName = typeMap.buy;
      } else if (normalizedType?.includes("bulk_memo")) {
        iconName = typeMap.memo;
      } else if (normalizedType?.includes("bulk_hold")) {
        iconName = typeMap.hold;
      } else if (normalizedType?.includes("bulk_quote")) {
        iconName = typeMap.quote;
      } else if (
        stockItemRequestCall.data?.data?.data?.stock_item_request
          ?.memo_status === MemoStatus.Sold
      ) {
        iconName = typeMap.sold;
      } else if (
        stockItemRequestCall.data?.data?.data?.stock_item_request
          ?.memo_status === MemoStatus.Return
      ) {
        iconName = typeMap.return;
      } else {
        const match = [normalizedFirst, normalizedLast].find(
          (part) => part && typeMap[part]
        );
        iconName = match ? typeMap[match] : null;
      }

      return (
        <VDBIcon
          name={iconName}
          size={24}
          color={theme["main-01"]}
          variant={IconsVariant.VDBCustom}
        />
      );
    }
  }, [
    stockItemRequestCall.data?.data?.data?.stock_item_request,
    stockItemRequestCall.data?.data?.response?.order,
    requestName,
    userInfo,
    theme,
  ]);

  if (stockItemRequestCall.isInitialLoading || getChildrenAPI.isLoading)
    return <Loader />;
  if (shouldWaitForOrderedItems) {
    return (
      <LayoutContainer>
        <ProcessingContainer>
          <LoaderBox>
            <Loader />
          </LoaderBox>
          <Spacer size={SpaceType.Basic} />
          <VDBText color={theme["core-05"]} type={typography["Body-R"]}>
            {t(
              "requestHistoryDetail.processing.pleaseWait.text",
              "Please wait.."
            )}
          </VDBText>
        </ProcessingContainer>
      </LayoutContainer>
    );
  }
  if (stockItemRequestCall.isError) return null;

  const checkBoxSelectionUpdate = (checked, mItem) => {
    if (!hasChildren) return;
    const matchingItem = stockItemRequest?.children?.find(
      (child) => child?.[productType.productType].id === mItem?.id
    );
    setSelectedItems((prevSelected) => {
      if (prevSelected.includes(matchingItem?.id)) {
        return prevSelected.filter((id) => id !== matchingItem?.id);
      } else {
        return [...prevSelected, matchingItem?.id];
      }
    });
  };

  return (
    <LayoutContainer>
      {!(
        requestName === RequestTypes.Order &&
        String(
          stockItemRequestCall.data?.data?.response?.order?.order_type ?? ""
        ).toLowerCase() === "quote" &&
        (stockItemRequestCall.data?.data?.response?.order?.view_type ===
          ViewType.SELLER ||
          (stockItemRequestCall.data?.data?.response?.order?.view_type ===
            ViewType.BUYER &&
            userInfo.enableOrderCartQuote() === true))
      ) && (
        <WebPageHeader
          hideBack={true}
          title={screenTitle}
          titleIcon={titleIcon}
        />
      )}
      {stockItemRequest && requestName !== RequestTypes.Order ? (
        <DynamicFormProvider>
          <MainContainer>
            <ScrollView
              style={{
                paddingHorizontal: theme.isMobile
                  ? SpaceType.Basic
                  : SpaceType.Double,
              }}
            >
              <Container maxWidth={1008} noPadding>
                <Spacer size={SpaceType.Double} />
                <RequestStatusCard
                  stockItemRequest={stockItemRequest}
                  isCustomApp={isCustomApp}
                />
                <Spacer size={SpaceType.Double} />
                {(showCompanyDetails ||
                  (stockItemRequest.view_type === "seller_view" &&
                    showRequestItemFromSupplier(stockItemRequest))) && (
                  <>
                    <CompanyDetailsCard
                      stockItemRequest={stockItemRequest}
                      productType={productType}
                      stockItemRequestParent={
                        route.params?.stockItemRequestParent
                      }
                    />
                    <Spacer size={SpaceType.Double} />
                  </>
                )}
                <StoreCustomerAttributes
                  attributes={
                    stockItemRequest?.additional_attributes
                      ?.store_customer_attributes
                  }
                />
                <CommentSection
                  requestType={stockItemRequest?.request_type}
                  comments={stockItemRequest?.comments}
                />
                <CardContainer>
                  <ProductListTopContainer background={productType.theme}>
                    <VDBIcon
                      variant={
                        icon?.type === IconsType.img_url ||
                        icon?.type === IconsType.url ||
                        matchedProduct?.icon?.type === IconsType.img_url ||
                        matchedProduct?.icon?.type === IconsType.url
                          ? IconsVariant.CustomUrl
                          : IconsVariant.VDBCustom
                      }
                      name={iconData}
                      uri={iconData}
                      size={SpaceType.Big}
                      color={theme[`${productType.theme}-06`]}
                    />
                    <Spacer size={SpaceType.ExtraSmall} />
                    <VDBText
                      type={typography["H3-M"]}
                      color={theme[`${productType.theme}-06`]}
                    >
                      {stockItemRequest?.[productType.productType]?.sub_type ===
                      ProductTypeCommon.LABGROWN_JEWELRY_SUB_TYPE
                        ? getProductName(ProductCategory.LABGROWN_JEWELRY, t)
                        : `${t(stockItemRequest.type_text)} ${
                            consolidatedStoneType.enabled
                              ? ""
                              : stockItemRequest.product_sub_type
                              ? stockItemRequest.product_sub_type
                              : ""
                          }`}
                    </VDBText>
                  </ProductListTopContainer>
                  <ItemsContainer>
                    {stockItemRequest?.request_type &&
                    !theme.isMobile &&
                    !route.params?.skip_unread &&
                    (getRequestType(stockItemRequest?.request_type) ===
                      RequestTypes.Quote ||
                      hasChildren) &&
                    !isBuilderItem &&
                    isCustomApp ? (
                      <>
                        <ItemActionCard
                          stockItemRequest={stockItemRequest}
                          productType={productType}
                          showListPrice={
                            !stockItemRequest?.additional_attributes
                              ?.total_offer_price_applied
                          }
                          isTotalOfferDiscountApplied={isTotalOfferDiscountApplied()}
                          hasAnyOfferDetails={hasAnyOfferDetails}
                          fromRequestDetailScreen={true}
                        />
                        <HorizontalScrollWrapper
                          isListView={theme.isLargeScreen || theme.isTablet}
                          xOffset={xOffset}
                          totalItems={stockItemRequest.children?.length}
                          selectedItems={selectedItems}
                          hideLeftArrow={true}
                          hasAnyOfferDetails={hasAnyOfferDetails}
                          fromSearchResults={false}
                          sectionName={
                            isPair(
                              stockItemRequest?.[
                                stockItemRequest?.stock_item_type
                              ]
                            )
                              ? SearchFilterTypeEnum.PAIRS
                              : isCustomApp &&
                                isParcel(
                                  stockItemRequest?.[
                                    stockItemRequest?.stock_item_type
                                  ]
                                )
                              ? SearchFilterTypeEnum.PARCEL
                              : SearchFilterTypeEnum.SINGLE_STONES
                          }
                        >
                          <Animated.FlatList
                            data={stockItemRequest.children}
                            // onScroll={scrollHandler}
                            stickyHeaderIndices={theme.isMobile ? [] : [0]}
                            renderItem={({ item, index }) => {
                              const productDetails = {
                                ...item?.[productType.productType],
                                offer_discount_percent:
                                  item.offer_discount_percent,
                                offer_price_per_carat:
                                  item.offer_price_per_carat,
                                offer_total_sales_price:
                                  item.offer_total_sales_price,
                                stock_id: item.id,
                                quantity: item.quantity,
                                offer_size: item.offer_size,
                                total_amount: item?.total_amount,
                              };

                              return (
                                <RequestDetailTabletListItem
                                  mItem={productDetails}
                                  index={index}
                                  stockItemRequest={item}
                                  stockItemRequestParent={stockItemRequest}
                                  checkBoxSelection={checkBoxSelectionUpdate}
                                  ismItemChecked={
                                    !!selectedItems.find(
                                      (_item) => _item === item?.id
                                    )
                                  }
                                  productType={productType.productType}
                                  removeFavorite={true}
                                  fromRequestDetail={true}
                                  priceMode={
                                    mExternalPriceMode || getUserPriceMode()
                                  }
                                  userCurrency={userCurrency}
                                  productPrefix={productPrefix}
                                  showListPrice={hideListPrice()}
                                  isTotalOfferDiscountApplied={isTotalOfferDiscountApplied()}
                                  request_type={item.view_type}
                                  hasAnyOfferDetails={hasAnyOfferDetails}
                                  viewType={stockItemRequest.request_type}
                                  sectionName={
                                    isPair(item?.[item?.stock_item_type])
                                      ? SearchFilterTypeEnum.PAIRS
                                      : isCustomApp &&
                                        isParcel(item?.[item?.stock_item_type])
                                      ? SearchFilterTypeEnum.PARCEL
                                      : SearchFilterTypeEnum.SINGLE_STONES
                                  }
                                />
                              );
                            }}
                            ListHeaderComponent={
                              !theme.isMobile &&
                              stockItemRequest.children &&
                              stockItemRequest.children?.length > 0 ? (
                                <TabletListHeader
                                  stockItemRequest={stockItemRequest}
                                  listMargin={17}
                                  productType={productType.productType}
                                  showCheckbox={false}
                                  showFavorite={false}
                                  fromRequestDetail={true}
                                  showListPrice={hideListPrice()}
                                  request_type={stockItemRequest.view_type}
                                  hasAnyOfferDetails={hasAnyOfferDetails}
                                  fromSearchResults={false}
                                  isTotalOfferDiscountApplied={isTotalOfferDiscountApplied()}
                                  viewType={stockItemRequest.request_type}
                                  isTotalOfferPriceApply={
                                    stockItemRequest?.additional_attributes
                                      ?.total_offer_price_applied
                                  }
                                  sectionName={
                                    isPair(
                                      stockItemRequest?.[
                                        stockItemRequest?.stock_item_type
                                      ]
                                    )
                                      ? SearchFilterTypeEnum.PAIRS
                                      : isCustomApp &&
                                        isParcel(
                                          stockItemRequest?.[
                                            stockItemRequest?.stock_item_type
                                          ]
                                        )
                                      ? SearchFilterTypeEnum.PARCEL
                                      : SearchFilterTypeEnum.SINGLE_STONES
                                  }
                                  selectedStoneType={
                                    isPair(
                                      stockItemRequest?.[
                                        stockItemRequest?.stock_item_type
                                      ]
                                    )
                                      ? StoneType.PAIR
                                      : isCustomApp &&
                                        isParcel(
                                          stockItemRequest?.[
                                            stockItemRequest?.stock_item_type
                                          ]
                                        )
                                      ? StoneType.PARCELS
                                      : StoneType.SINGLE_STONES
                                  }
                                />
                              ) : null
                            }
                            // ListEmptyComponent={
                            //   !cartApiLoading && dataUnavailable ? (
                            //     <EmptyView />
                            //   ) : null
                            // }
                          />
                        </HorizontalScrollWrapper>
                      </>
                    ) : (
                      <RequestViewParentCard
                        stockItemRequest={stockItemRequest}
                        productType={productType}
                        validValue={validValue}
                        onClickSupplierInfo={() =>
                          handleSupplierForPaUser(
                            stockItemRequest?.deep_link ?? "",
                            navigate,
                            productPrefix
                          )
                        }
                        setDisabledButton={setDisabledButton}
                        fromRequestDetail={true}
                        showListPrice={hideListPrice()}
                        isTotalOfferDiscountApplied={isTotalOfferDiscountApplied()}
                        hasAnyOfferDetails={hasAnyOfferDetails}
                        childValues={childValues}
                        skipUnread={route.params?.skip_unread}
                        params={route.params}
                      />
                    )}
                  </ItemsContainer>
                </CardContainer>
                <Spacer size={SpaceType.Double} />
                {getRequestType(stockItemRequest?.request_type) !==
                  RequestTypes.Quote &&
                  reasonContainer.title &&
                  !hasChildren && (
                    <>
                      <ReasonContainer
                        type={reasonContainer.type}
                        title={reasonContainer.title}
                        applyShadow
                      >
                        <VDBText
                          color={theme["core-06"]}
                          type={typography["Body-R"]}
                        >
                          {reasonContainer.comments}
                        </VDBText>
                      </ReasonContainer>
                      <Spacer size={SpaceType.Double} />
                    </>
                  )}
                <TotalPriceCard
                  stockItemRequest={stockItemRequest}
                  productType={productType}
                  hasChildren={hasChildren}
                  childRequest={route.params?.skip_unread}
                  hasAnyOfferDetails={hasAnyOfferDetails}
                  showListPrice={hideListPrice()}
                  isBuilderItem={isBuilderItem}
                  isLooseParcel={isLooseParcel}
                />
                {getRequestType(stockItemRequest?.request_type) ===
                  RequestTypes.Quote &&
                  !theme.isMobile &&
                  (stockItemRequest?.status === "decline" ||
                    stockItemRequest?.status === "withdraw") && (
                    <ActionCard
                      skipUnread={route.params?.skip_unread}
                      stockItemRequest={stockItemRequest}
                    />
                  )}
                {theme.isTablet || theme.isLargeScreen ? (
                  (stockItemRequest?.memo_status ||
                    (userInfo.enableSellReturnButton() &&
                      getRequestType(stockItemRequest?.request_type) ===
                        RequestTypes.Memo)) &&
                  stockItemRequest?.view_type === ViewType.BUYER ? (
                    <></>
                  ) : (
                    (stockItemRequest?.parent_id === null ||
                      stockItemRequest?.view_type === ViewType.SELLER) && (
                      <>
                        <StyledButtonsContainer
                          stockItemRequest={stockItemRequest}
                          productType={productType}
                          disabledButton={disabledButton}
                          modifyRequest={modifyRequest}
                          selectedItems={selectedItems}
                          isHideOtherButtonVisible={isHideOtherButtonVisible()}
                          onSubmitChange={(data: any) => {
                            const enrichedRequest = enrichStockItemRequest(
                              data,
                              data.stock_item_type,
                              t,
                              isCustomApp
                            );
                            setStockItemRequest(
                              enrichedRequest || {
                                ...data,
                                type_text:
                                  requestTypeMap(t)[data.stock_item_type]
                                    ?.name || "",
                              }
                            );
                          }}
                          isBuilderItem={isBuilderItem}
                        />
                        <Spacer size={SpaceType.Size_80} />
                      </>
                    )
                  )
                ) : (
                  <></>
                )}
              </Container>
            </ScrollView>
            {theme.isMobile ? (
              (stockItemRequest?.memo_status ||
                (userInfo.enableSellReturnButton() &&
                  getRequestType(stockItemRequest?.request_type) ===
                    RequestTypes.Memo)) &&
              stockItemRequest?.view_type === ViewType.BUYER ? (
                <></>
              ) : (
                (stockItemRequest?.parent_id === null ||
                  stockItemRequest?.view_type === ViewType.SELLER) && (
                  <>
                    <LineViewContainer
                      background="core-03"
                      marginLeft={false}
                    />
                    <StyledButtonsContainer
                      stockItemRequest={stockItemRequest}
                      productType={productType}
                      modifyRequest={modifyRequest}
                      disabledButton={disabledButton}
                      onSubmitChange={(data: any) => {
                        const enrichedRequest = enrichStockItemRequest(
                          data,
                          data.stock_item_type,
                          t,
                          isCustomApp
                        );
                        setStockItemRequest(
                          enrichedRequest || {
                            ...data,
                            type_text:
                              requestTypeMap(t)[data.stock_item_type]?.name ||
                              "",
                          }
                        );
                      }}
                      isBuilderItem={isBuilderItem}
                    />
                  </>
                )
              )
            ) : (
              <></>
            )}
            {Platform.OS === "ios" && theme.isTablet && <FooterSpace />}
          </MainContainer>
        </DynamicFormProvider>
      ) : requestName === RequestTypes.Order ? (
        <OrderRequestScreen
          requestName={requestName}
          theme={theme}
          stockItemRequest={normalizedData}
          isCustomApp={isCustomApp}
          showCompanyDetails={showCompanyDetails}
          productType={productType}
          iconData={iconData}
          typography={typography}
          validValue={validValue}
          productPrefix={productPrefix}
          hasChildren={hasChildren}
          setDisabledButton={setDisabledButton}
          modifyRequest={modifyRequest}
          disabledButton={disabledButton}
          selectedItems={selectedItems}
          isHideOtherButtonVisible={isHideOtherButtonVisible()}
          isBuilderItem={false}
        />
      ) : (
        ""
      )}
    </LayoutContainer>
  );
};

const MainContainer = styled.View`
  flex: 1;
  background-color: ${({ theme }) => theme["core-02"]};
  padding-bottom: ${({ theme }) => (theme.isMobile ? 0 : SpaceType.large)}px;
`;

const ProcessingContainer = styled.View`
  flex: 1;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme["core-02"]};
`;

const LoaderBox = styled.View`
  width: 34px;
  height: 34px;
`;

const FooterSpace = styled.View`
  padding: 10px;
  height: ${Platform.OS === "ios" ? 34 : 10}px;
  background-color: ${({ theme }) => theme["core-01"]};
`;

const StyledButtonsContainer = styled(ButtonsContainer)`
  border-radius: 8px;
  box-shadow: ${({ theme }) => getBoxShadow(theme.Shadows.Gentle)};
`;
const CardContainer = styled.View`
  background-color: ${({ theme }) => theme["core-01"]};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme["core-03"]};
  box-shadow: ${({ theme }) => getBoxShadow(theme.Shadows.Gentle)};
`;

const ItemsContainer = styled.View`
  padding-bottom: ${SpaceType.Basic}px;
  padding-left: ${({ theme }) =>
    theme.isMobile ? `${SpaceType.Basic}px` : `${SpaceType.Double}px`};
  padding-right: ${({ theme }) =>
    theme.isMobile ? `${SpaceType.Basic}px` : `${SpaceType.Double}px`};
`;

const ProductListTopContainer = styled.View<{ background: string }>`
  background-color: ${({ background, theme }) => theme[`${background}-03`]};
  padding: ${SpaceType.Extra}px ${SpaceType.Extra}px;
  flex-direction: row;
  align-items: center;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
`;

export default (props: any) => (
  <SearchResultProvider>
    <SelectedItemsProvider>
      <RequestDetailScreen {...props} />
    </SelectedItemsProvider>
  </SearchResultProvider>
);
